import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setPageSize,
} from "../../../../redux/zaloEvent/zaloEvent";
import {
  getListZaloEvent,
  updateZaloEvent,
  deleteZaloEvent,
} from "../../../../api/zaloEvent/zaloEvent";
import { NavLink } from "react-router-dom";
import {
  SaveOutlined,
  CloseOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Table,
  Spin,
  Form,
  Input,
  message,
  Col,
  Row,
  Card,
} from "antd";
import FormAddZaloEvent from "./FormAddZaloEvent";
import RowActions from "../../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../../components/cms/actions";
import { neutral } from "../../../../theme/tokens";

export default function ZaloEventTable() {
  const {
    listZaloEvents,
    isLoadZaloEvent,
    currentPage,
    pageSize,
    totalRecords,
  } = useSelector((state) => state.persistedReducer.zaloEventSlice);

  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [editedRecord, setEditedRecord] = useState({});

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  useEffect(() => {
    dispatch(getListZaloEvent({ currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleSizeChange = useCallback(
    (current, size) => {
      setPageSize(size);
    },
    [setPageSize]
  );

  // Kiểm tra xem dòng có đang được chỉnh sửa không
  const isEditing = (record) => record.id === editingKey;

  // Bắt đầu chỉnh sửa
  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
    setEditedRecord(record);
  };

  // Hủy chỉnh sửa
  const cancel = () => {
    setEditingKey("");
  };

  // Lưu dữ liệu đã chỉnh sửa
  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const updatedData = { ...row };
      const response = await dispatch(
        updateZaloEvent({ id, data: updatedData })
      );
      if (updateZaloEvent.fulfilled.match(response)) {
        message.success("Sửa thành công");
        await dispatch(getListZaloEvent({ currentPage, pageSize }));
      } else if (updateZaloEvent.rejected.match(response)) {
        if (response.payload.message) {
          message.error(response.payload.message);
        }
        if (response.payload.errors) {
          Object.keys(response.payload.errors).forEach((field) => {
            const errorMessages = response.payload.errors[field];
            errorMessages.forEach((errorMessage) => {
              message.error(`${errorMessage}`);
            });
          });
        }
      }
      setEditingKey("");
    } catch (err) {
      console.error("Có lỗi xảy ra:", err);
    }
  };

  const deleteEventRecord = async (id) => {
    try {
      await dispatch(deleteZaloEvent(id)); // Gọi API xóa
      dispatch(getListZaloEvent({ currentPage, pageSize })); // Lấy lại danh sách sau khi xóa
    } catch (err) {
      console.error("Error deleting service:", err);
    }
  };

  // Định nghĩa các cột cho bảng
  const columns = [
    {
      title: "Mã sự kiện",
      dataIndex: "code",
      key: "code",
      fixed: 'left',
      width: 200,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="code"
            rules={[
              {
                required: true,
                message: "Mã sự kiện",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.code
        );
      },
    },
    {
      title: "Tên sự kiện",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="name"
            rules={[
              {
                required: true,
                message: "Tên sự kiện",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.name
        );
      },
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record); // Kiểm tra nếu dòng đang chỉnh sửa
        return editable ? (
          <>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record.id)} // Lưu dữ liệu
              style={{ marginRight: 8 }}
            >
              Lưu
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={cancel} // Hủy chỉnh sửa
              danger
              style={{ color: neutral[500] }}
            >
              Hủy
            </Button>
          </>
        ) : (
          <RowActions
            // Khóa cả cụm khi đang sửa dòng khác — giữ đúng hành vi cũ.
            disabled={editingKey !== ""}
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                onClick: () => edit(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa sự kiện",
                danger: true,
                divider: true,
                onClick: () =>
                  confirmDelete(record.name || `sự kiện #${record.id}`, () =>
                    deleteEventRecord(record.id)
                  ),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <div>
      <div className="bg-gray-50 min-h-screen p-6">
        <Card className="mb-6 shadow-sm">
          <Breadcrumb separator=">">
            <Breadcrumb.Item>Trang chủ</Breadcrumb.Item>
            <Breadcrumb.Item>Dịch vụ</Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="/zalo-events">Sự kiện Zalo</NavLink>
            </Breadcrumb.Item>
          </Breadcrumb>
          <Row gutter={[16, 16]}>
            <Col xl lg md sm>
              <Button
                type="primary"
                onClick={showModal}
                icon={<PlusCircleOutlined />}
              >
                Thêm mới
              </Button>
              <FormAddZaloEvent
                isModalOpen={isModalOpen}
                currentPage={currentPage}
                pageSize={pageSize}
                handleOk={handleOk}
                handleCancel={handleCancel}
              />
            </Col>
          </Row>
          <Divider />

          <Form form={form} component={false}>
            <Table
              columns={columns}
              loading={listZaloEvents?.length === 0 ? isLoadZaloEvent : false}
              dataSource={isLoadZaloEvent ? [] : listZaloEvents} // Dữ liệu hiển thị trong bảng
              rowKey="id"
              pagination={{
                showTotal: (totalRecords, range) =>
                  `Hiển thị từ ${range[0]} -> ${range[1]} trong tổng ${totalRecords} giá trị`,
                showQuickJumper: true,
                showSizeChanger: true,
                onShowSizeChange: handleSizeChange,
                total: totalRecords,
                current: currentPage,
                onChange: handlePageChange,
                pageSizeOptions: [10, 20, 30],
              }}
              locale={{
                emptyText: <Empty description="Không có sự kiện!" />,
              }}
              size="small"
              scroll={{ x: "max-content" }}
            />
          </Form>
        </Card>
      </div>
    </div>
  );
}
